import '@/polyfills';

// Styles have to be imported before the components so that the CSS layers
// are defined in the correct order
import '@/styles/all.css';

import { createPinia } from 'pinia';

import * as Sentry from '@sentry/vue';

import RiskLedgerPlugin from '@/plugins/risk-ledger';

import { isReportableError } from '@/api/errors';
import store from '@/store';
import { storeInjectionKey } from '@/store/useStore';
import router from '@/routes';
import App from './App.vue';

const app = createApp(App);

app.use(createPinia());

app.use(router);

app.use(store, storeInjectionKey);

app.use(RiskLedgerPlugin);

app.config.errorHandler = (error: unknown) => {
  if (import.meta.env.MODE !== 'prod') {
    // oxlint-disable-next-line no-console
    console.error(error);
  }
  logError(error);
};

Sentry.init({
  app,
  dsn: 'https://65661bf8d5fa04ddee40133c122dbda4@o4507701844901888.ingest.de.sentry.io/4507980359663696',
  integrations: [
    Sentry.replayIntegration({
      // Mask values of <input> elements. By default, masks text with *
      // @see: https://docs.sentry.io/platforms/javascript/session-replay/privacy/#masking
      maskAllInputs: true,

      maskAllText: false,
      blockAllMedia: false,

      block: ['[data-private]'],
    }),
    // Need this to enable Canvas replays
    Sentry.replayCanvasIntegration(),

    // @see: https://docs.sentry.io/platforms/javascript/guides/vue/features/component-tracking/#trackcomponents
    Sentry.vueIntegration({
      tracingOptions: {
        trackComponents: true,
      },
    }),
  ],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  enabled: import.meta.env.MODE === 'prod',
  environment: import.meta.env.MODE,

  ignoreErrors: [
    /** @see https://github.com/getsentry/sentry-javascript/issues/3440 */
    /^Non-Error promise rejection captured with value: Object Not Found Matching Id:.+, MethodName:.+, ParamCount:.+$/,
  ],

  // Drop expected, handled client errors: 4xx ApiErrors already surfaced to the
  // user at the call site (e.g. signup "user_already_exists") but still bubbling
  // to the global errorHandler and Sentry's Vue integration. This is the one
  // choke point every capture path passes through; genuine 5xx, network, and
  // non-ApiError failures still report.
  //
  // Transport failures are filtered here rather than via ignoreErrors so the
  // decision can depend on the request: 'NetworkError' in ignoreErrors also
  // discarded edge-blocked mutations, which is how RL-8043 went unseen.
  beforeSend(event, hint) {
    if (!isReportableError(hint.originalException)) return null;
    return event;
  },
});

async function bootstrapMocks() {
  // `import.meta.env.MODE` is statically replaced at build time, so Vite
  // tree-shakes this branch (and the whole `@/libs/msw/*` graph) out of prod.
  if (import.meta.env.MODE === 'prod') return;
  const { initMockWorker } = await import('@/libs/msw/boot');
  initMockWorker();
}

void Promise.all([router.isReady(), bootstrapMocks()]).then(() => {
  app.mount('#app');
});
